import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Activity,
  Wifi,
  UserX,
  Users,
  CheckCircle,
  AlertTriangle,
  Trash2,
  CheckSquare,
  Square,
  Trash,
  Search,
  Filter,
} from 'lucide-react';
import PaginationControls from '../../components/admin/PaginationControls';

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  useEffect(() => {
    loadSessions();

    const interval = setInterval(loadSessions, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, pageSize]);

  const loadSessions = async () => {
    try {
      const response = await adminAPI.getSessions();
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      toast.error(error.response?.data?.error || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = useMemo(() => {
    return sessions.filter((session) => {
      const matchesSearch = !searchTerm || [
        session.user_name,
        session.client_ip,
        session.plan_name,
      ].some((value) => String(value || '').toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === 'all' || session.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [sessions, searchTerm, statusFilter]);

  const paginatedSessions = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSessions.slice(start, start + pageSize);
  }, [filteredSessions, page, pageSize]);

  const kickSession = async (sessionId, userName) => {
    if (!window.confirm(`Are you sure you want to kick ${userName}?`)) {
      return;
    }

    try {
      await adminAPI.kickSession(sessionId);
      toast.success(`${userName} has been disconnected`);
      loadSessions();
    } catch (error) {
      console.error('Failed to kick session:', error);
      toast.error(error.response?.data?.error || `Failed to disconnect ${userName}`);
    }
  };

  const deleteSession = async (sessionId, userName, sessionStatus) => {
    const action = sessionStatus === 'active' ? 'kick and delete' : 'delete';
    if (!window.confirm(`Are you sure you want to ${action} ${userName}'s session?`)) {
      return;
    }

    try {
      if (sessionStatus === 'active') {
        await adminAPI.kickSession(sessionId);
      }
      await adminAPI.deleteSession(sessionId);
      toast.success(`${userName}'s session has been deleted`);
      loadSessions();
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error(error.response?.data?.error || `Failed to delete session for ${userName}`);
    }
  };

  const toggleSessionSelection = (sessionId) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const toggleSelectAll = () => {
    const pageIds = paginatedSessions.map((session) => session.id);
    const allSelected = pageIds.every((sessionId) => selectedSessions.has(sessionId));
    const nextSelected = new Set(selectedSessions);

    if (allSelected) {
      pageIds.forEach((sessionId) => nextSelected.delete(sessionId));
    } else {
      pageIds.forEach((sessionId) => nextSelected.add(sessionId));
    }

    setSelectedSessions(nextSelected);
  };

  const bulkDeleteSessions = async () => {
    if (selectedSessions.size === 0) {
      toast.error('Please select sessions to delete');
      return;
    }

    const selectedSessionDetails = sessions.filter((session) => selectedSessions.has(session.id));
    if (!window.confirm(`Are you sure you want to delete ${selectedSessionDetails.length} selected session(s)?`)) {
      return;
    }

    try {
      const results = await Promise.all(
        selectedSessionDetails.map(async (session) => {
          try {
            if (session.status === 'active') {
              await adminAPI.kickSession(session.id);
            }
            await adminAPI.deleteSession(session.id);
            return { success: true };
          } catch (error) {
            return { success: false, error };
          }
        })
      );

      const failedCount = results.filter((result) => !result.success).length;
      const successCount = results.length - failedCount;

      if (successCount > 0) {
        toast.success(`${successCount} session(s) deleted successfully`);
      }
      if (failedCount > 0) {
        toast.error(`Failed to delete ${failedCount} session(s)`);
      }

      setSelectedSessions(new Set());
      loadSessions();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete sessions');
    }
  };

  const deleteAllSessions = async () => {
    if (filteredSessions.length === 0) {
      toast.error('No sessions to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${filteredSessions.length} session(s) in the current result set?`)) {
      return;
    }

    try {
      await Promise.all(
        filteredSessions.map(async (session) => {
          if (session.status === 'active') {
            await adminAPI.kickSession(session.id);
          }
          await adminAPI.deleteSession(session.id);
        })
      );
      toast.success('Sessions deleted successfully');
      setSelectedSessions(new Set());
      loadSessions();
    } catch (error) {
      console.error('Delete all failed:', error);
      toast.error(error.response?.data?.error || 'Failed to delete all sessions');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDuration = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const duration = Math.floor((now - start) / 1000);
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const getTimeRemaining = (expiresAt) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const remaining = Math.floor((expires - now) / 1000);

    if (remaining <= 0) return 'Expired';

    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);

    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-green-50 text-green-700';
      case 'expired':
        return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-red-50 text-red-700';
      case 'closed':
        return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-gray-50 text-gray-700';
      default:
        return 'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-gray-50 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4" />;
      case 'expired':
        return <AlertTriangle className="h-4 w-4" />;
      case 'closed':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  return (
    <div className="p-8">
      <div className="bg-white border-b border-gray-200 px-8 py-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Activity className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Sessions</h1>
              <p className="text-sm text-gray-600 mt-1">
                Monitor active and expired sessions with responsive cards, search, filters, and paged results.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {paginatedSessions.length > 0 ? (
              <>
                <button type="button" onClick={toggleSelectAll} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  <CheckSquare className="h-4 w-4" />
                  <span>Select Page</span>
                </button>

                {selectedSessions.size > 0 ? (
                  <button type="button" onClick={bulkDeleteSessions} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700">
                    <Trash2 className="h-4 w-4" />
                    <span>Delete ({selectedSessions.size})</span>
                  </button>
                ) : null}

                <button type="button" onClick={deleteAllSessions} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700">
                  <Trash className="h-4 w-4" />
                  <span>Delete Filtered</span>
                </button>
              </>
            ) : null}

            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded bg-green-50 text-green-700">
              <span className="h-2 w-2 rounded-full bg-current" />
              <span>Live refresh every 30s</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search user, IP, or plan"
              className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none">
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-600 mt-4">Loading sessions...</p>
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gray-100 rounded-lg">
              <Users className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{sessions.length === 0 ? 'No sessions available' : 'No sessions match your filters'}</h3>
          <p className="text-gray-600">
            {sessions.length === 0
              ? 'Live session cards will appear here when users connect through the VPN.'
              : 'Adjust the search or filter settings to see more sessions.'}
          </p>
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-3">
            {paginatedSessions.map((session) => {
              const usagePercent = session.bytes_quota
                ? Math.min((session.bytes_used / session.bytes_quota) * 100, 100)
                : 0;

              return (
                <article key={session.id} className="bg-white rounded-lg border border-gray-200 p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex min-w-0 items-start gap-3">
                      <button
                        type="button"
                        onClick={() => toggleSessionSelection(session.id)}
                        className="p-1 rounded hover:bg-gray-50 shrink-0"
                        title="Select session"
                      >
                        {selectedSessions.has(session.id) ? <CheckSquare className="h-4 w-4 text-blue-600" /> : <Square className="h-4 w-4 text-gray-400" />}
                      </button>

                      <div className="p-2 bg-gray-100 rounded-lg shrink-0">
                        {getStatusIcon(session.status)}
                      </div>

                      <div className="min-w-0">
                        <h2 className="truncate text-lg font-semibold text-gray-900">{session.user_name}</h2>
                        <p className="break-all font-mono text-sm text-gray-600">{session.client_ip}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={getStatusClass(session.status)}>
                        {getStatusIcon(session.status)}
                        <span>{session.status}</span>
                      </span>

                      {session.status === 'active' ? (
                        <button
                          type="button"
                          onClick={() => kickSession(session.id, session.user_name)}
                          className="p-1.5 rounded hover:bg-gray-50 text-gray-400 hover:text-gray-600"
                          title="Disconnect user"
                        >
                          <UserX className="h-4 w-4" />
                        </button>
                      ) : null}

                      <button
                        type="button"
                        onClick={() => deleteSession(session.id, session.user_name, session.status)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600"
                        title="Delete session"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="rounded-lg bg-gray-50 p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-md">
                            <Wifi className="h-4 w-4 text-gray-600" />
                          </div>
                          <span className="text-sm text-gray-600">Plan</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{session.plan_name}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="rounded-lg bg-gray-50 p-4">
                        <p className="text-sm text-gray-600">Duration</p>
                        <p className="mt-1 font-semibold text-gray-900">{formatDuration(session.start_at)}</p>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-4">
                        <p className="text-sm text-gray-600">Time Left</p>
                        <p className="mt-1 font-semibold text-gray-900">{getTimeRemaining(session.expires_at)}</p>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-4">
                        <p className="text-sm text-gray-600">Data Used</p>
                        <p className="mt-1 font-semibold text-gray-900">{formatBytes(session.bytes_used)}</p>
                      </div>
                    </div>

                    {session.bytes_quota ? (
                      <div className="rounded-lg bg-white border border-gray-200 p-4">
                        <div className="mb-3 flex items-center justify-between gap-3 text-sm">
                          <span className="text-gray-600">Quota</span>
                          <span className="font-semibold text-gray-900">{formatBytes(session.bytes_quota)}</span>
                        </div>
                        <div className="h-3 overflow-hidden rounded-full bg-gray-200">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600"
                            style={{ width: `${usagePercent}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs text-gray-600">{Math.round(usagePercent)}% used</p>
                      </div>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </section>

          <PaginationControls
            page={page}
            pageSize={pageSize}
            totalItems={filteredSessions.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            itemLabel="sessions"
          />
        </>
      )}
    </div>
  );
};

export default Sessions;
